/* Copy Pack dialog — shows what goes into the ONE clipboard payload for a
 * build stage, with include/exclude checkboxes per dependency doc and a live
 * size estimate. Copy lands the whole pack via navigator.clipboard. */
import React, {
    useEffect,
    useMemo,
    useState
} from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from 'shared/components/ui/dialog';
import {
    Checkbox
} from 'shared/components/ui/checkbox';
import {
    BookOpenText,
    ClipboardCopy,
    Loader2
} from 'lucide-react';
import {
    notify
} from 'shared/api/notifier';
import {
    useAppStudio
} from '../AppStudioContext';
import {
    assemblePack,
    fetchGuide,
    loadStageDeps,
    sizeEstimate,
    STAGE_LABELS
} from './copyPack';
import type {
    GuideInfo,
    PackCurrentDoc,
    PackDep,
    PackStage
} from './copyPack';

function CheckRow({
    checked,
    onChange,
    label,
    meta
}: {
    checked: boolean;
    onChange: (next: boolean) => void;
    label: string;
    meta?: string;
}) {
    return (
        <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            padding: '6px 2px',
            cursor: 'pointer'
        }}>
            <Checkbox checked={checked} onCheckedChange={(v) => onChange(v === true)} />
            <span style={{
                flex: 1,
                minWidth: 0,
                fontSize: 'var(--aps-text-sm)',
                color: 'var(--aps-text)'
            }}>
                {label}
            </span>
            {meta ? (
                <span className="aps-mono" style={{
                    fontSize: 'var(--aps-text-2xs)',
                    color: 'var(--aps-text-muted)'
                }}>
                    {meta}
                </span>
            ) : null}
        </label>
    );
}

export default function CopyPackDialog({
    stage,
    currentDoc,
    open,
    onOpenChange,
    onCopied
}: {
    stage: PackStage;
    currentDoc?: PackCurrentDoc | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Fires after a successful copy (dialog already closing) — the Build
     * tab uses it to open the paste-result panel. */
    onCopied?: () => void;
}) {
    const {
        tenancy
    } = useAppStudio();
    const [guide, setGuide] = useState<GuideInfo | null>(null);
    const [deps, setDeps] = useState<PackDep[] | null>(null);
    const [checked, setChecked] = useState<Record<string, boolean>>({});
    const [includeCurrent, setIncludeCurrent] = useState(true);
    const [includeGuide, setIncludeGuide] = useState(true);

    // Load guide + stage dependencies fresh on every open (deps are live
    // documents). fetchGuide never rejects (it falls back); dep loads
    // degrade to [].
    useEffect(() => {
        if (!open) return;
        let alive = true;
        setGuide(null);
        setDeps(null);
        setIncludeCurrent(true);
        setIncludeGuide(true);
        Promise.all([
            fetchGuide(tenancy.org_id),
            loadStageDeps(tenancy, stage).catch(() => [] as PackDep[])
        ])
            .then(([g, d]) => {
                if (!alive) return;
                setGuide(g);
                setDeps(d);
                const all: Record<string, boolean> = {};
                for (const dep of d) all[dep.key] = true;
                setChecked(all);
            });
        return () => {
            alive = false;
        };
    }, [open, tenancy, stage]);

    const payload = useMemo(() => {
        if (guide == null || deps == null) return null;
        return assemblePack({
            stage,
            guide,
            deps: deps.filter((d) => checked[d.key]),
            currentDoc: includeCurrent ? currentDoc : null,
            includeGuide
        });
    }, [guide, deps, checked, includeCurrent, includeGuide, currentDoc, stage]);

    const copy = async () => {
        if (payload == null) return;
        try {
            await navigator.clipboard.writeText(payload);
            notify('Copy pack copied — paste it into your LLM chat', 'success');
            onOpenChange(false);
            onCopied?.();
        } catch {
            notify('Clipboard unavailable — check the browser permission', 'warning');
        }
    };

    const loading = guide == null || deps == null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[520px]">
                <DialogHeader>
                    <DialogTitle style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                    }}>
                        <BookOpenText size={17} color="var(--aps-primary)" />
                        Copy pack — {STAGE_LABELS[stage]} stage
                    </DialogTitle>
                    <DialogDescription>
                        One self-contained clipboard payload: the authoring
                        guide, the stage&apos;s dependency documents (lean
                        projections) and a request scaffold. Paste it into any
                        fresh LLM chat.
                    </DialogDescription>
                </DialogHeader>
                {loading ? (
                    <div className="aps-empty" style={{
                        padding: '26px 10px'
                    }}>
                        <Loader2 size={20} className="aps-spin" />
                        <span>Assembling the pack…</span>
                    </div>
                ) : (
                    <div style={{
                        maxHeight: '46vh',
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2
                    }}>
                        <CheckRow checked={includeGuide}
                            onChange={setIncludeGuide}
                            label="Include the authoring guide"
                            meta={sizeEstimate(guide.markdown.length)} />
                        {deps.length === 0 && (
                            <p className="aps-help" style={{
                                margin: '4px 0 0'
                            }}>
                                This stage carries no dependency documents — the
                                guide plus the request scaffold is the whole pack.
                            </p>
                        )}
                        {deps.map((d) => (
                            <CheckRow key={d.key}
                                checked={!!checked[d.key]}
                                onChange={(v) => setChecked((c) => ({
                                    ...c,
                                    [d.key]: v
                                }))}
                                label={d.label}
                                meta={sizeEstimate(JSON.stringify(d.doc, null, 2).length)} />
                        ))}
                        {currentDoc && (
                            <CheckRow checked={includeCurrent}
                                onChange={setIncludeCurrent}
                                label={`Current document — ${currentDoc.label}`}
                                meta={sizeEstimate(currentDoc.text.length)} />
                        )}
                    </div>
                )}
                <DialogFooter style={{
                    alignItems: 'center',
                    gap: 10
                }}>
                    <span className="aps-mono" style={{
                        marginRight: 'auto',
                        fontSize: 'var(--aps-text-xs)',
                        color: 'var(--aps-text-muted)'
                    }}>
                        {payload == null ? '…' : sizeEstimate(payload.length)}
                    </span>
                    <button className="aps-btn aps-btn-ghost"
                        onClick={() => onOpenChange(false)}>
                        Cancel
                    </button>
                    <button className="aps-btn aps-btn-primary"
                        disabled={payload == null} onClick={copy}>
                        <ClipboardCopy size={15} />
                        Copy pack
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
